/**
 * DriverProblemPanel — « Un problème ? » sur l'accueil conducteur web (2026-09-14).
 * =============================================================================
 * Repris de l'onglet Urgence de l'APK, élagué à trois gestes :
 *   - appeler l'assistance (le contact que l'organisation porte — `tel:`) ;
 *   - déclarer un sinistre : type, date, heure, lieu (position du navigateur en
 *     un clic, sinon saisie), ce qui s'est passé, photo facultative, « le
 *     véhicule roule encore » ; le serveur rattache la garde de pool en cours ;
 *   - un défaut sur le véhicule ? Il se signale au RETOUR, dans la passation,
 *     avec le vocabulaire fermé : on le dit ici plutôt que d'ouvrir un
 *     deuxième formulaire de défaut.
 *
 * On ne recopie pas l'onglet Entretien de l'APK : en pool, la révision et le
 * contrôle technique sont l'affaire du gestionnaire (mémo autopartage §4.4,
 * « entretien orphelin ») — l'agent signale, il ne planifie pas.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { LifeBuoy, Phone, AlertTriangle, MapPin, X, Check } from 'lucide-react';
import { driverSelfAPI } from '../../../api/drivers.js';

/** Énum `claim.type` du schéma (yaml_cli enum claim.type) — l'ordre est celui de l'APK. */
export const CLAIM_TYPES = ['collision', 'breakdown', 'glass_breakage', 'theft', 'hail', 'fire', 'bodily_injury', 'other'];

const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const nowInput = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function ClaimForm({ onDone, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    type: 'collision', event_date: todayInput(), event_time: nowInput(), location: '', description: '', drivable: true, photo: null,
  });
  const [gps, setGps] = useState(null);       // { lat, lon, accuracy, at }
  const [gpsState, setGpsState] = useState('idle'); // idle | asking | denied
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const locate = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) { setGpsState('denied'); return; }
    setGpsState('asking');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy), at: new Date().toISOString() });
        setGpsState('idle');
      },
      () => setGpsState('denied'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const create = useMutation({
    mutationFn: (fd) => driverSelfAPI.createClaim(fd),
    onSuccess: (res) => onDone?.(res?.data || null),
    onError: (err) => setError(err?.message || t('poolDriver.problem.errorGeneric')),
  });

  const submit = (e) => {
    e.preventDefault();
    setError(null);
    const fd = new FormData();
    fd.append('type', form.type);
    fd.append('event_date', form.event_date);
    if (form.event_time) fd.append('event_time', form.event_time);
    if (form.location) fd.append('location', form.location);
    if (form.description) fd.append('description', form.description);
    fd.append('drivable', form.drivable ? 'true' : 'false');
    if (gps) {
      fd.append('gps_lat', String(gps.lat));
      fd.append('gps_lon', String(gps.lon));
      fd.append('captured_at', gps.at);
    }
    if (form.photo) fd.append('photos', form.photo);
    create.mutate(fd);
  };

  const field = 'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500';

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-red-200 bg-red-50/40 p-3" data-testid="driver-claim-form">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-900">{t('poolDriver.problem.claimTitle')}</p>
        <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-800" aria-label={t('poolDriver.problem.cancel')}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <fieldset>
        <legend className="text-sm text-gray-700">{t('poolDriver.problem.whatHappened')}</legend>
        <div className="mt-1 grid grid-cols-2 gap-1.5">
          {CLAIM_TYPES.map((k) => (
            <label key={k} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm ${form.type === k ? 'border-red-400 bg-white font-medium text-red-800' : 'border-gray-200 text-gray-700'}`}>
              <input type="radio" name="claim_type" value={k} checked={form.type === k} onChange={set('type')} className="h-3.5 w-3.5" />
              {t(`poolDriver.problem.types.${k}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-gray-700">
          {t('poolDriver.problem.date')}
          <input type="date" value={form.event_date} onChange={set('event_date')} max={todayInput()} className={field} required />
        </label>
        <label className="text-sm text-gray-700">
          {t('poolDriver.problem.time')}
          <input type="time" value={form.event_time} onChange={set('event_time')} className={field} />
        </label>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-2.5">
        <div className="flex items-center justify-between gap-2">
          <p className="flex items-center gap-1 text-sm text-gray-700"><MapPin className="h-4 w-4 text-red-500" /> {t('poolDriver.problem.where')}</p>
          <button type="button" onClick={locate} disabled={gpsState === 'asking'} className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
            {gpsState === 'asking' ? t('poolDriver.problem.locating') : t('poolDriver.problem.locate')}
          </button>
        </div>
        {gps && (
          <p className="mt-1 text-xs text-gray-500" data-testid="claim-gps">
            {gps.lat.toFixed(5)}, {gps.lon.toFixed(5)} · ±{gps.accuracy} m — {t('poolDriver.problem.gpsAttached')}
          </p>
        )}
        {gpsState === 'denied' && <p className="mt-1 text-xs text-gray-500">{t('poolDriver.problem.gpsDenied')}</p>}
        <input type="text" value={form.location} onChange={set('location')} placeholder={t('poolDriver.problem.wherePlaceholder')} className={field} />
      </div>

      <label className="block text-sm text-gray-700">
        {t('poolDriver.problem.description')}
        <textarea value={form.description} onChange={set('description')} rows={3} className={field} />
      </label>

      <label className="block text-sm text-gray-700">
        {t('poolDriver.problem.photo')}
        <input type="file" accept="image/*" capture="environment" onChange={(e) => setForm((f) => ({ ...f, photo: e.target.files?.[0] || null }))} className="mt-1 block w-full text-sm text-gray-600" />
      </label>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={form.drivable} onChange={set('drivable')} className="h-4 w-4 rounded border-gray-300 text-red-600" />
        {t('poolDriver.problem.drivable')}
      </label>

      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <button
        type="submit"
        disabled={create.isPending}
        className="flex min-h-[48px] w-full items-center justify-center rounded-lg bg-red-700 px-4 py-2.5 text-base font-semibold text-white hover:bg-red-800 disabled:opacity-60"
      >
        {create.isPending ? t('poolDriver.problem.sending') : t('poolDriver.problem.send')}
      </button>
    </form>
  );
}

export function DriverProblemPanel({ custody = null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(null);

  const contacts = useQuery({
    queryKey: ['driver-emergency-contacts'],
    queryFn: () => driverSelfAPI.getEmergencyContacts(),
    staleTime: 60 * 60_000,
    retry: false,
  });
  const list = contacts.data?.ok ? contacts.data.data?.contacts || [] : [];
  const primary = list.find((c) => c.phone) || null;

  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-testid="driver-problem-panel">
      <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
        <LifeBuoy className="h-4 w-4 text-red-600" />
        {t('poolDriver.problem.title')}
      </h2>

      {primary && (
        <a
          href={`tel:${String(primary.phone).replace(/\s+/g, '')}`}
          className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border-2 border-red-300 bg-white px-4 py-2.5 text-base font-semibold text-red-700 hover:bg-red-50"
          data-testid="call-assistance"
        >
          <Phone className="h-5 w-5" />
          {t('poolDriver.problem.call', { name: primary.name || t('poolDriver.problem.fleetManager') })}
          <span className="font-mono text-sm font-normal text-red-600">{primary.phone}</span>
        </a>
      )}

      {sent ? (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="claim-sent">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <div>
            <p className="font-medium">{t('poolDriver.problem.sent', { number: sent.claim_number || sent.id || '' })}</p>
            <p className="text-emerald-800">{t('poolDriver.problem.sentHint')}</p>
          </div>
          <button type="button" onClick={() => setSent(null)} className="ml-auto text-emerald-700" aria-label={t('poolDriver.problem.cancel')}><X className="h-4 w-4" /></button>
        </div>
      ) : open ? (
        <ClaimForm onDone={(claim) => { setOpen(false); setSent(claim || {}); }} onCancel={() => setOpen(false)} />
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-red-700 px-4 py-2.5 text-base font-semibold text-white hover:bg-red-800"
        >
          <AlertTriangle className="h-5 w-5" />
          {t('poolDriver.problem.declare')}
        </button>
      )}

      <p className="text-xs text-gray-500">
        {custody ? t('poolDriver.problem.defectHintCustody') : t('poolDriver.problem.defectHint')}
      </p>
    </section>
  );
}

export default DriverProblemPanel;
